// 261010 Epic 2 / P2b-3 — thẻ SỬA MỘT SHOT, trích NGUYÊN VĂN từ EventPlanTab.tsx (move-only) để dùng chung với khung mới.
// Kèm các hằng chỉ thẻ này dùng (opts camera). Thay đổi DUY NHẤT so với bản gốc: onMove/onDelete/canUp/canDown tuỳ chọn + điều kiện render cụm nút cấu trúc (xem chú thích tại prop).
import React from 'react';
import { Button, Popconfirm, Select, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { FilmDialogueLine, FilmShot } from '../../types/film';

const { Text } = Typography;

// Giữ thuật ngữ chuẩn (tiếng Anh) làm label; phần giải thích tiếng Việt nằm ở tooltip (option + field).
// label giữ English thuần (search/hiển thị đúng); hint tiếng Việt để ở `title` → hover từng option hiện giải thích.
const withHint = (pairs: [string, string][]) => pairs.map(([value, hint]) => ({ value, label: value, title: hint }));
// 260723: opts LƯU MÃ ENUM (value) nhưng HIỂN THỊ nhãn đẹp (label) — value phải khớp filmCameraVocab
// của backend, nếu không toSeedancePhrase tra map không ra (bug move/angle "tracking"/"High angle").
const withLabel = (triples: [string, string, string][]) => triples.map(([value, label, title]) => ({ value, label, title }));
const SIZE_OPTS = withHint([
  ['EWS', 'Extreme Wide Shot — the whole space, very wide'], ['WS', 'Wide Shot — full body + setting'],
  ['MWS', 'Medium Wide Shot — more than half the body'], ['MS', 'Medium Shot — from the hips up'],
  ['MCU', 'Medium Close-Up — chest to head'], ['CU', 'Close-Up — the face'], ['ECU', 'Extreme Close-Up — detail (eyes/details)'],
]);
const PURPOSE_OPTS = withHint([
  ['ESTABLISH', 'Establish — introduce the space'], ['REVEAL', 'Reveal something new'], ['REACT', 'Character reaction'],
  ['TURN', 'Turn in the story'], ['RESOLVE', 'Resolve the scene'],
]);
// Mã enum CameraAngle (backend): EYE|LOW|HIGH|BIRD|DUTCH|GROUND. OTS/POV KHÔNG phải angle → nằm ở FRAMING.
const ANGLE_OPTS = withLabel([
  ['EYE', 'Eye level', 'Ngang tầm mắt (trung tính) — 87% shot phim thật'],
  ['LOW', 'Low angle', 'Hắt từ dưới lên (quyền lực, áp đảo)'],
  ['HIGH', 'High angle', 'Chúc từ trên xuống (yếu thế)'],
  ['BIRD', "Bird's-eye", 'Nhìn thẳng từ trên cao xuống'],
  ['DUTCH', 'Dutch angle', 'Máy nghiêng (bất an)'],
  ['GROUND', 'Ground level', 'Sát mặt đất nhìn lên'],
]);
// Epic2-camera fields (260722): framing (trục TRỰC GIAO với angle) + effect (modifier quang học).
const FRAMING_OPTS = withHint([
  ['OTS', 'Over-the-shoulder — giữ CẢ 2 người trong khung (chuẩn đối thoại phim thật ~66%)'],
  ['POV', 'Point of view — nhìn qua mắt nhân vật'],
  ['TWO_SHOT', 'Two-shot — 2 nhân vật cùng khung'],
  ['GROUP', 'Group — nhóm nhân vật'],
]);
const EFFECT_OPTS = withHint([
  ['RACK_FOCUS', 'Rack focus — chuyển nét giữa 2 lớp'],
  ['SLOW_MO', 'Slow motion'],
  ['MACRO', 'Macro — cận chi tiết cực đại'],
  ['FRAME_IN_FRAME', 'Frame-in-frame — khung lồng khung (cửa/gương)'],
  ['REFLECTION', 'Reflection — quay qua phản chiếu'],
]);
// Mã enum CameraMove (backend, 15 mã). MỘT động tác/shot (gộp 2 = jitter). Bỏ các nhãn cũ trùng nghĩa
// (pan left/right → PAN · dolly/zoom in → PUSH_IN · crane up → AERIAL) mà backend vốn đã clamp về cùng mã.
const MOVE_OPTS = withLabel([
  ['STATIC', 'Static', 'Máy cố định'],
  ['PUSH_IN', 'Push in', 'Đẩy chậm về phía chủ thể'],
  ['PULL_OUT', 'Pull out', 'Kéo máy ra'],
  ['PAN', 'Pan', 'Lia ngang'],
  ['TRACKING', 'Tracking', 'Bám theo chủ thể'],
  ['ORBIT', 'Orbit', 'Xoay quanh chủ thể'],
  ['HANDHELD', 'Handheld', 'Cầm tay (rung nhẹ)'],
  ['AERIAL', 'Aerial', 'Trên không / crane'],
  ['TILT_UP', 'Tilt up', 'Ngẩng máy lên'],
  ['TILT_DOWN', 'Tilt down', 'Chúc máy xuống'],
  ['WHIP_PAN', 'Whip pan', 'Lia rất nhanh (chuyển cảnh)'],
  ['DOLLY_ZOOM', 'Dolly zoom', 'Dolly zoom (hiệu ứng chóng mặt)'],
  ['CRANE_DOWN', 'Crane down', 'Hạ crane xuống'],
  ['PAN_360', '360° pan', 'Lia trọn vòng'],
  ['SNAP_ZOOM', 'Snap zoom', 'Zoom giật nhanh'],
]);
const SIZE_HINT = 'Shot size — framing distance: EWS (very wide) → WS → MS → CU → ECU (extreme close-up). Vertical video favors CU/MCU to show emotion clearly.';
const PURPOSE_HINT = 'Purpose — the shot\'s role in the beat: ESTABLISH (set the space) · REVEAL · REACT · TURN · RESOLVE.';

export const ShotCard: React.FC<{
  shot: FilmShot; onSave: (id: string, d: Record<string, unknown>) => void;
  // 261010 P2b-3: onMove/onDelete (+ canUp/canDown) TUỲ CHỌN — thiếu thì KHÔNG render cụm ↑ ↓ xoá shot (khung mới không có thao tác cấu trúc). UI cũ vẫn truyền đủ.
  onDelete?: (id: string) => void; onMove?: (id: string, dir: -1 | 1) => void; canUp?: boolean; canDown?: boolean;
  cast: string[]; dirty?: boolean;
  // Thoại của event + shot nào đang giữ câu nào — để gán tay câu ↔ shot (xem lineOpts).
  lines?: FilmDialogueLine[]; lineTaken?: Map<number, { shotId: string; order: number }>;
}> = ({ shot, onSave, onDelete, onMove, canUp, canDown, cast, dirty, lines, lineTaken }) => {
  const sd: any = shot.screenDirection || {};
  const p: any = shot.presence || {};
  const setFocal = (v: string) => onSave(shot.id, { presence: { ...p, focal: v } });
  const setVisible = (v: string[]) => onSave(shot.id, { presence: { ...p, visible: v } });
  const setSd = (patch: Record<string, unknown>) => onSave(shot.id, { screenDirection: { ...sd, ...patch } });
  const castOpts = cast.map((n) => ({ label: n, value: n }));
  /**
   * Gán câu thoại vào shot (`presence.speaksDialogueIndex`, 1-based theo event).
   *
   * Trước 260812 trường này CHỈ do AI directing đặt và UI chỉ hiện một Tag đọc-thôi ⇒ AI bỏ sót là chịu.
   * Đo prod 30 ngày: 55/413 event có thoại mà KHÔNG shot nào được gán (13,3%); riêng plan 7203ea4a event#4
   * cả 4 shot đều null. Backend vốn đã lưu nguyên object `presence` nên chỉ thiếu đúng cái ô này.
   *
   * Ràng buộc 1 câu ↔ 1 shot (linter `dialogue-double-shot` là mức error): câu đã có shot khác giữ thì
   * hiện disabled kèm tên shot đang giữ, thay vì để chọn rồi ăn lỗi lint sau.
   */
  const lineOpts = (lines || []).map((d, i) => {
    const holder = lineTaken?.get(i + 1);
    const taken = !!holder && holder.shotId !== shot.id;
    return {
      value: i + 1,
      disabled: taken,
      label: `#${i + 1} ${d.speakerName || '?'}: "${String(d.textEn || '').slice(0, 28)}"${taken ? ` — đã gán shot #${holder!.order}` : ''}`,
    };
  });
  return (
    <div className={`sb-shotedit${dirty ? ' dirty' : ''}`}>
      <div className="se-head">
        <span className="se-num">#{shot.orderIndex}</span>
        {dirty && <Tag color="warning" style={{ margin: 0 }}>● unsaved</Tag>}
        <span className="se-field"><span className="se-lab">size</span>
          <Tooltip title={SIZE_HINT}><Select size="small" placeholder="size" value={shot.shotSize || undefined} options={SIZE_OPTS} style={{ width: 78 }} onChange={(v) => onSave(shot.id, { shotSize: v })} /></Tooltip></span>
        <span className="se-field"><span className="se-lab">angle</span>
          <Tooltip title="Camera angle relative to the character (hover each option for meaning)"><Select size="small" showSearch placeholder="angle" value={shot.angle || undefined} options={ANGLE_OPTS} style={{ width: 138 }} onChange={(v) => onSave(shot.id, { angle: v })} /></Tooltip></span>
        <span className="se-field"><span className="se-lab">move</span>
          <Tooltip title="Camera move — one motion per shot (hover each option for meaning)"><Select size="small" showSearch placeholder="move" value={shot.cameraMove || undefined} options={MOVE_OPTS} style={{ width: 134 }} onChange={(v) => onSave(shot.id, { cameraMove: v })} /></Tooltip></span>
        <span className="se-field"><span className="se-lab">framing</span>
          <Tooltip title="Framing — trục trực giao với angle: OTS/POV/two-shot/group (OTS + low angle + push-in cùng lúc là hợp lệ)"><Select size="small" allowClear placeholder="—" value={(shot as any).framing || undefined} options={FRAMING_OPTS} style={{ width: 104 }} onChange={(v) => onSave(shot.id, { framing: v ?? null })} /></Tooltip></span>
        <span className="se-field"><span className="se-lab">effect</span>
          <Tooltip title="Effect — modifier quang học (không thay camera move)"><Select size="small" allowClear placeholder="—" value={(shot as any).effect || undefined} options={EFFECT_OPTS} style={{ width: 128 }} onChange={(v) => onSave(shot.id, { effect: v ?? null })} /></Tooltip></span>
        <span className="se-field"><span className="se-lab">purpose</span>
          <Tooltip title={PURPOSE_HINT}><Select size="small" placeholder="purpose" value={shot.purpose || undefined} options={PURPOSE_OPTS} style={{ width: 112 }} onChange={(v) => onSave(shot.id, { purpose: v })} /></Tooltip></span>
        {lineOpts.length > 0 && (
          <span className="se-field"><span className="se-lab">🗣 line</span>
            <Tooltip title="Câu thoại được nói ở shot NÀY (1 câu ↔ 1 shot). Bỏ trống = shot không có lời — nhưng câu nào không gán shot nào thì prompt phải tự đoán, và giây per-shot bị rải đều.">
              <Select size="small" allowClear placeholder="—" value={p.speaksDialogueIndex ?? undefined}
                options={lineOpts} style={{ width: 168 }} optionLabelProp="label"
                onChange={(v) => onSave(shot.id, { presence: { ...p, speaksDialogueIndex: v ?? null } })} />
            </Tooltip></span>
        )}
        <span className="se-spacer" />
        {onMove && (<>
        <Tooltip title="Move shot earlier"><Button type="text" size="small" disabled={!canUp} onClick={() => onMove(shot.id, -1)}>↑</Button></Tooltip>
        <Tooltip title="Move shot later"><Button type="text" size="small" disabled={!canDown} onClick={() => onMove(shot.id, 1)}>↓</Button></Tooltip>
        </>)}
        {onDelete && (
        <Popconfirm title="Delete this shot?" onConfirm={() => onDelete(shot.id)} okText="Delete" cancelText="Cancel">
          <Tooltip title="Delete shot"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
        </Popconfirm>
        )}
      </div>
      <div className="se-head" style={{ marginBottom: 0 }}>
        <span className="se-field"><span className="se-lab">focal</span>
          <Tooltip title="Focal — the character/object the shot centers on"><Select size="small" showSearch allowClear placeholder="focal" value={p.focal || undefined} options={castOpts} style={{ width: 128 }} onChange={setFocal} /></Tooltip></span>
        <span className="se-field"><span className="se-lab">visible</span>
          <Tooltip title="Visible — everyone appearing in frame (pick from cast or type more)"><Select size="small" mode="tags" tokenSeparators={[',']} placeholder="visible" value={p.visible || []} options={castOpts} style={{ minWidth: 168 }} onChange={setVisible} /></Tooltip></span>
      </div>
      <div className="se-details">
        <span className="k">Frame</span>
        <span className="v"><Text style={{ fontSize: 11.5 }} editable={{ text: shot.frameNote ?? '', onChange: (v) => onSave(shot.id, { frameNote: v }), tooltip: 'Edit frame composition' }}>{shot.frameNote || <span style={{ color: 'var(--sb-faint)' }}>(frame note)</span>}</Text></span>
        <span className="k">Camera</span>
        <span className="v"><Text style={{ fontSize: 11.5 }} editable={{ text: sd.cameraPosition ?? '', onChange: (v) => setSd({ cameraPosition: v }), tooltip: 'Where the camera sits and points' }}>{sd.cameraPosition || <span style={{ color: 'var(--sb-faint)' }}>(camera position)</span>}</Text></span>
        <span className="k">Background</span>
        <span className="v"><Text style={{ fontSize: 11.5 }} editable={{ text: sd.background ?? '', onChange: (v) => setSd({ background: v }), tooltip: 'Background behind the subject' }}>{sd.background || <span style={{ color: 'var(--sb-faint)' }}>(background)</span>}</Text></span>
        {sd.axis && <><span className="k">Axis</span><span className="v" style={{ color: 'var(--sb-soft)' }}>{sd.axis}</span></>}
      </div>
    </div>
  );
};

export default ShotCard;
